import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, BookOpen, Lock, Play, RotateCcw, Sparkles, Star } from "lucide-react";
import api from "@/lib/api";
import { playSay } from "@/lib/readingAudio";
import { ReadingLockedNote } from "@/components/ReadingUpsell";
import { isReadingApp } from "@/lib/readingApp";

const T = {
  en: { name: "English Reading", test: "Take the test", begin: "Start at the beginning", cont: "Continue", demo: "Try a free lesson",
    locked: "Ask your parent to unlock English Reading", reviews: (n) => `${n} quick review${n > 1 ? "s" : ""} waiting`, of: (a, b) => `${a}/${b} lessons` },
  fr: { name: "Lecture en français", test: "Faire le test", begin: "Commencer au début", cont: "Continuer", demo: "Essayer une leçon gratuite",
    locked: "Demande à tes parents de débloquer la Lecture", reviews: (n) => `${n} petite${n > 1 ? "s" : ""} révision${n > 1 ? "s" : ""} à faire`, of: (a, b) => `${a}/${b} leçons` },
};

export const ReadingBg = ({ petit, children }) => (
  <div className={`min-h-screen font-kids text-slate-900 ${petit ? "bg-[#FFF6D6]" : "bg-[#EEF2F7]"}`}>
    {petit && <div className="pointer-events-none fixed inset-0 opacity-40" style={{ backgroundImage: "radial-gradient(#FACC15 1.5px, transparent 1.5px)", backgroundSize: "28px 28px" }} />}
    <div className="relative">{children}</div>
  </div>
);

function LangCard({ lang, info, petit, go, onStart }) {
  const t = T[lang];
  const pct = Math.round((info.mastered / info.total) * 100);
  return (
    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: lang === "en" ? 0.1 : 0.25 }}
      whileHover={{ y: -4 }} data-testid={`reading-lang-card-${lang}`}
      className={`rounded-[2rem] border-[3px] border-slate-900 p-6 shadow-[8px_8px_0_0_#0F172A] flex flex-col gap-4 ${lang === "en" ? "bg-sky-200" : "bg-pink-200"}`}>
      <div className="flex items-center gap-3">
        <span className="text-4xl">{lang === "en" ? "🇬🇧" : "🇫🇷"}</span>
        <div className="flex-1">
          <h2 className="text-2xl sm:text-3xl font-bold">{t.name}</h2>
          <p className="font-semibold text-slate-700">{t.of(info.mastered, info.total)} · <Star size={14} className="inline -mt-1 fill-yellow-400 text-yellow-500" /> {info.stars}</p>
        </div>
      </div>
      <div className="h-4 rounded-full bg-white border-2 border-slate-900 overflow-hidden">
        <motion.div className="h-full bg-green-400" initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 1 }} />
      </div>
      {info.reviews_due.length > 0 && <p className="text-sm font-bold text-orange-700" data-testid={`reading-reviews-${lang}`}><RotateCcw size={14} className="inline -mt-0.5" /> {t.reviews(info.reviews_due.length)}</p>}
      {info.next_title && info.access && <p className="font-semibold" data-testid={`reading-next-${lang}`}>{petit ? "➡️ " : ""}{info.next_title}</p>}
      <div className="flex flex-wrap gap-3">
        {info.access && !info.started && (
          <>
            <Btn onClick={() => go(`/kid/${info.childId}/reading/${lang}/test`)} testid={`reading-test-${lang}`} color="bg-blue-600 text-white"><Sparkles size={18} /> {t.test}</Btn>
            <Btn onClick={() => onStart(lang)} testid={`reading-begin-${lang}`} color="bg-white"><Play size={18} /> {t.begin}</Btn>
          </>
        )}
        {info.access && info.started && <Btn onClick={() => go(`/kid/${info.childId}/reading/${lang}`)} testid={`reading-continue-${lang}`} color="bg-green-500 text-white"><Play size={18} /> {t.cont}</Btn>}
        {!info.access && (
          <>
            <Btn onClick={() => go(`/kid/${info.childId}/reading/lesson/${info.demo_lesson}`)} testid={`reading-demo-${lang}`} color="bg-yellow-300"><BookOpen size={18} /> {t.demo}</Btn>
            <p className="w-full text-sm font-semibold text-slate-700 flex items-center gap-1"><Lock size={14} /> {t.locked}</p>
          </>
        )}
      </div>
    </motion.div>
  );
}

export const Btn = ({ children, onClick, color, testid }) => (
  <motion.button whileTap={{ scale: 0.95 }} onClick={onClick} data-testid={testid}
    className={`rounded-full border-[3px] border-slate-900 px-5 h-12 font-bold shadow-[4px_4px_0_0_#0F172A] inline-flex items-center gap-2 ${color}`}>
    {children}
  </motion.button>
);

const KITS = [
  { id: "petit", title: "Kit Petit lecteur", sub: "Little reader · Kiko, big pictures and games", color: "bg-yellow-300" },
  { id: "grand", title: "Kit Grand lecteur", sub: "Big reader · same lessons, calmer and faster", color: "bg-sky-300" },
];

// Both reading kits are open to every child, whatever the grade; stars and progress are shared.
function KitSwitch({ style, onPick }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-8" data-testid="reading-kits">
      {KITS.map((k) => (
        <motion.button key={k.id} whileHover={{ y: -3 }} whileTap={{ scale: 0.97 }} onClick={() => style !== k.id && onPick(k.id)} data-testid={`reading-kit-${k.id}`}
          className={`rounded-3xl border-[3px] border-slate-900 p-3 sm:p-4 text-left transition-shadow ${style === k.id ? `${k.color} shadow-[5px_5px_0_0_#0F172A]` : "bg-white/80 opacity-80"}`}>
          <p className="text-base sm:text-xl font-bold flex items-center gap-2">{k.title} {style === k.id && <span className="text-xs rounded-full bg-slate-900 text-white px-2 py-0.5" data-testid={`reading-kit-active-${k.id}`}>✓</span>}</p>
          <p className="text-xs sm:text-sm text-slate-700 mt-1">{k.sub}</p>
        </motion.button>
      ))}
    </div>
  );
}

export default function ReadingHome() {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const load = () => api.get(`/reading/${childId}/home`).then(({ data }) => setData(data)).catch(() => navigate(isReadingApp() ? "/lecture" : `/kid/${childId}`));
  useEffect(() => { load(); }, [childId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return <ReadingBg petit><div className="p-10 text-center font-bold">…</div></ReadingBg>;
  const petit = data.style === "petit";
  const start = async (lang) => {
    await api.post(`/reading/${childId}/${lang}/start-beginning`);
    navigate(`/kid/${childId}/reading/${lang}`);
  };
  const hello = petit ? "Hi! I'm Kiko. Let's learn to read together!" : "Welcome to Reading skills. Pick a language.";
  return (
    <ReadingBg petit={petit}>
      <header className="max-w-5xl mx-auto px-5 pt-5 flex items-center gap-3">
        <button onClick={() => navigate(isReadingApp() ? "/lecture" : `/kid/${childId}`)} className="w-11 h-11 rounded-full bg-white border-[3px] border-slate-900 flex items-center justify-center" data-testid="reading-back"><ArrowLeft /></button>
        <h1 className="text-3xl sm:text-4xl font-bold" data-testid="reading-title">{petit ? "Let's read! · On lit !" : "Reading skills"}</h1>
      </header>
      <main className="max-w-5xl mx-auto px-5 py-8">
        <motion.button onClick={() => playSay(hello, "en")} initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }}
          className="flex items-end gap-3 mb-8 text-left" data-testid="reading-kiko-hello">
          <motion.img src="/mascot/kiko.png" alt="Kiko" className={petit ? "w-32 h-32" : "w-20 h-20"} animate={{ rotate: [0, -6, 6, 0] }} transition={{ repeat: Infinity, duration: 2.4 }} />
          <span className="rounded-3xl rounded-bl-none bg-white border-[3px] border-slate-900 px-5 py-3 text-lg sm:text-xl font-bold shadow-[4px_4px_0_0_#0F172A] max-w-md">
            {petit ? `Hi ${data.child.name}! I'm Kiko 🦎 Let's learn to read! · Salut ! On apprend à lire ?` : `Hi ${data.child.name}! Choose English or French.`}
          </span>
        </motion.button>
        <KitSwitch style={data.style} onPick={async (st) => { await api.post(`/reading/${childId}/style`, { style: st }); load(); }} />
        <div className="grid md:grid-cols-2 gap-6">
          {["en", "fr"].map((lang) => (
            <LangCard key={lang} lang={lang} petit={petit} info={{ ...data.langs[lang], childId }} go={navigate} onStart={start} />
          ))}
        </div>
        {(!data.langs.en.access || !data.langs.fr.access) && <ReadingLockedNote paidCount={data.child.paid_count || 0} />}
      </main>
    </ReadingBg>
  );
}
